import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  MapPin,
  Filter,
  Scale,
  Clock,
  UtensilsCrossed,
  AlertTriangle,
  ChevronRight,
  RefreshCw
} from "lucide-react";

const WageLaws = () => {
  const navigate = useNavigate();
  const [wageLaws, setWageLaws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("all");
  const [selectedLawType, setSelectedLawType] = useState("all");
  const [expandedLaw, setExpandedLaw] = useState(null);

  const fetchWageLaws = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await apiClient.get(`/api/wage-laws`);
      setWageLaws(response.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Failed to fetch wage laws:", error);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchWageLaws();
  }, [fetchWageLaws]);

  // Get unique states and law types for filters
  const uniqueStates = useMemo(() => {
    const states = [...new Set(wageLaws.map(law => law.StateName).filter(Boolean))];
    return states.sort();
  }, [wageLaws]);

  const uniqueLawTypes = useMemo(() => {
    const types = [...new Set(wageLaws.map(law => law.ViolationType).filter(Boolean))];
    return types.sort();
  }, [wageLaws]);

  // Filter and search
  const filteredLaws = useMemo(() => {
    return wageLaws.filter(law => {
      const matchesSearch = !searchTerm || 
        law.StateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        law.ViolationType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        law.StatuteSummary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        law.StatuteReference?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesState = selectedState === "all" || law.StateName === selectedState;
      const matchesType = selectedLawType === "all" || law.ViolationType === selectedLawType;
      
      return matchesSearch && matchesState && matchesType;
    });
  }, [wageLaws, searchTerm, selectedState, selectedLawType]);

  // Group by state for display
  const groupedByState = useMemo(() => {
    const groups = {};
    filteredLaws.forEach(law => {
      const state = law.StateName || "Unknown";
      if (!groups[state]) {
        groups[state] = [];
      }
      groups[state].push(law);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredLaws]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedState("all");
    setSelectedLawType("all");
  };

  const hasActiveFilters = searchTerm || selectedState !== "all" || selectedLawType !== "all";

  const getLawTypeIcon = (type) => {
    switch (type) {
      case "Overtime":
        return <Clock className="w-4 h-4" />;
      case "DoubleTime":
        return <AlertTriangle className="w-4 h-4" />;
      case "Meal Break":
        return <UtensilsCrossed className="w-4 h-4" />;
      case "Rest Break":
        return <Clock className="w-4 h-4" />;
      default:
        return <Scale className="w-4 h-4" />;
    }
  };

  const getLawTypeBadge = (type) => {
    const colors = {
      "Overtime": "bg-blue-500",
      "DoubleTime": "bg-purple-500",
      "Meal Break": "bg-amber-500",
      "Rest Break": "bg-teal-500"
    };
    return (
      <Badge className={`${colors[type] || "bg-gray-500"} text-white text-xs px-2 py-0.5`}>
        {type}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading wage laws...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 h-[calc(100vh-3.5rem)] flex flex-col animate-fade-in" data-testid="wage-laws-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
            State Wage Laws
          </h1>
          <p className="text-sm text-muted-foreground">
            {filteredLaws.length} laws across {groupedByState.length} states
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchWageLaws(true)}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 flex-shrink-0">
        {uniqueLawTypes.map(type => {
          const count = wageLaws.filter(l => l.ViolationType === type).length;
          return (
            <Card 
              key={type} 
              className={`p-3 cursor-pointer hover:bg-muted/50 transition-colors ${selectedLawType === type ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setSelectedLawType(selectedLawType === type ? "all" : type)}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center flex-shrink-0">
                  {getLawTypeIcon(type)}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{type}</p>
                  <p className="text-xl font-bold font-['Manrope']">{count}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="mb-4 flex-shrink-0">
        <CardContent className="py-3 px-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search laws, statutes, references..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>

            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="w-[180px]" data-testid="state-filter">
                <MapPin className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {uniqueStates.map((state) => (
                  <SelectItem key={state} value={state}>{state}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedLawType} onValueChange={setSelectedLawType}>
              <SelectTrigger className="w-[160px]" data-testid="type-filter">
                <Scale className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {uniqueLawTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <Filter className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Laws List */}
      <Card className="flex-1 flex flex-col min-h-0" data-testid="wage-laws-list">
        <CardContent className="p-0 flex-1 overflow-hidden">
          <div className="h-full overflow-auto">
            {groupedByState.length > 0 ? (
              <div className="divide-y divide-border">
                {groupedByState.map(([state, laws]) => (
                  <div key={state} className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-primary" />
                      <h3 className="font-semibold text-base">{state}</h3>
                      <Badge variant="secondary" className="text-xs">{laws.length} laws</Badge>
                    </div>
                    <div className="grid gap-2 pl-6">
                      {laws.map((law, idx) => (
                        <div
                          key={`${state}-${law.ViolationType}-${idx}`}
                          className={`p-3 rounded-lg border bg-card hover:bg-muted/50 cursor-pointer transition-colors ${expandedLaw === `${state}-${law.ViolationType}-${idx}` ? 'ring-1 ring-primary' : ''}`}
                          onClick={() => setExpandedLaw(expandedLaw === `${state}-${law.ViolationType}-${idx}` ? null : `${state}-${law.ViolationType}-${idx}`)}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {getLawTypeBadge(law.ViolationType)}
                                {law.IsApplicable && (
                                  <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-600">
                                    Applicable
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {law.StatuteSummary || "No summary available"}
                              </p>
                              
                              {/* Expanded Details */}
                              {expandedLaw === `${state}-${law.ViolationType}-${idx}` && (
                                <div className="mt-3 pt-3 border-t space-y-3">
                                  <div>
                                    <p className="text-xs font-medium text-muted-foreground mb-1">Statute Reference</p>
                                    <p className="text-sm">{law.StatuteReference || "N/A"}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-muted-foreground mb-1">Damage Formula</p>
                                    <p className="text-sm">{law.DamageFormulaDescription || "N/A"}</p>
                                  </div>
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <div className="p-2 bg-muted/50 rounded">
                                      <p className="text-xs text-muted-foreground">Wage Multiplier</p>
                                      <p className="font-medium">{law.WageMultiplier || "N/A"}</p>
                                    </div>
                                    <div className="p-2 bg-muted/50 rounded">
                                      <p className="text-xs text-muted-foreground">Liquidated Damages</p>
                                      <p className="font-medium">{law.LiquidatedDamagesMultiplier || "N/A"}x</p>
                                    </div>
                                    <div className="p-2 bg-muted/50 rounded">
                                      <p className="text-xs text-muted-foreground">Min Penalty</p>
                                      <p className="font-medium">${law.CivilPenaltyMinPerViolation || "0"}</p>
                                    </div>
                                    <div className="p-2 bg-muted/50 rounded">
                                      <p className="text-xs text-muted-foreground">Max Penalty</p>
                                      <p className="font-medium">${law.CivilPenaltyMaxPerViolation || "0"}</p>
                                    </div>
                                  </div>
                                  {law.StatuteVerbose && (
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground mb-1">Full Statute Details</p>
                                      <p className="text-sm text-muted-foreground">{law.StatuteVerbose}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <ChevronRight className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${expandedLaw === `${state}-${law.ViolationType}-${idx}` ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                {hasActiveFilters ? "No matching wage laws found" : "No wage laws available"}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WageLaws;
